import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { API_BASE, api, type LandingFacts } from '../lib/api'
import { Logo } from '../components/Logo'
import { RegionOutline } from './LandingPage'
import { useSession } from '../lib/session'

/**
 * Sign in.
 *
 * The page's argument is on the left and the form is on the right, because a
 * visitor arriving here has usually just been stopped by something: a page
 * image they could not open, a full-text link on a search result. The left
 * half says what signing in gives them, with the archive's real figures and a
 * real page beside it, so the form is a means rather than a gate.
 *
 * Nobody is asked to sign in to *search*. That is the discovery tier, it costs
 * no seat, and the link to it stays visible on this page.
 */
export function LoginPage() {
  const { config, signIn, signInError, signingIn } = useSession()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [reveal, setReveal] = useState(false)
  const [keep, setKeep] = useState(true)

  const facts = useQuery({
    queryKey: ['landing'],
    queryFn: () => api.get<LandingFacts>('/landing'),
    staleTime: 10 * 60 * 1000,
  })
  const f = facts.data

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    try {
      // A workstation in a reading room is shared. Unticking the box marks
      // this browser session as ephemeral, and the session provider ends it
      // when the tab closes rather than leaving a signed-in seat behind.
      try {
        if (keep) sessionStorage.removeItem('jl.ephemeral')
        else sessionStorage.setItem('jl.ephemeral', '1')
      } catch {
        // Storage disabled: the refresh token expires on its own schedule.
      }
      await signIn(email, password)
    } catch {
      // Surfaced through signInError; nothing to do here.
    }
  }

  return (
    <div className="flex min-h-full flex-col bg-paper">
      <header className="flex h-16 flex-none items-center gap-6 border-b border-rule px-[--edge]">
        <Link to="/" className="flex items-center gap-2.5 text-body no-underline">
          <Logo size={22} />
          <span className="text-[17px] font-semibold tracking-[-0.005em]">
            {config?.product_name ?? ''}
          </span>
        </Link>
        <div className="ml-auto flex items-center gap-6 text-[13px] uppercase tracking-[0.08em]">
          <Link to="/" className="hidden sm:inline">
            Coverage
          </Link>
          <Link to="/#libraries" className="hidden sm:inline">
            For libraries
          </Link>
          <Link to="/search">Search as a guest</Link>
        </div>
      </header>

      <div className="mx-auto grid w-full max-w-[1200px] flex-1 grid-cols-1 items-start gap-[clamp(40px,6vw,96px)] px-[--edge] pb-[calc(2*var(--leading))] pt-[calc(2.5*var(--leading))] lg:grid-cols-[1fr_452px]">
        <div>
          <h1 className="-ml-[0.035em] mb-[--leading] text-[clamp(40px,5.4vw,72px)] font-semibold leading-[1.02] tracking-[-0.02em]">
            <span className="block">Sign in to see</span>
            <span className="block">the page itself.</span>
          </h1>
          <p className="mb-[calc(1.5*var(--leading))] max-w-[52ch] text-[17px] leading-[--leading] text-ink2">
            You are already able to search. Access adds the page image at full resolution, the
            recognised text, clippings and citation export.
          </p>

          <hr className="m-0 h-[5px] border-0 border-b border-t-2 border-ink" />
          <div className="flex flex-wrap justify-between gap-[--half] gap-x-[--leading] py-[--half] text-[13px] uppercase leading-[--half] tracking-[0.08em] text-ink2">
            <div>{f?.publication ?? '-'}</div>
            <div>{f?.year_range ?? '-'}</div>
            <div>{f ? `${f.editions.toLocaleString()} editions` : '-'}</div>
            <div>{f ? `${f.pages.toLocaleString()} pages` : '-'}</div>
          </div>
          <hr className="m-0 h-0 border-0 border-t border-ink" />

          <div className="mt-[calc(1.5*var(--leading))] grid grid-cols-1 gap-[clamp(28px,4vw,56px)] sm:grid-cols-[1.05fr_1fr]">
            {f?.sample && (
              <div>
                <div className="bg-well p-3.5">
                  <div className="relative">
                    <img
                      src={`${API_BASE}/${f.sample.image_url}`}
                      alt={`Page ${f.sample.page_label} of the ${f.sample.date} edition`}
                      className="block w-full"
                    />
                    {f.sample.regions.map((region, i) => (
                      <RegionOutline key={i} region={region} />
                    ))}
                  </div>
                </div>
                <div className="ident mt-2.5 uppercase leading-relaxed">
                  {f.sample.date} · {f.sample.page_label}
                  <br />
                  {f.sample.geometry}
                </div>
              </div>
            )}
            <div>
              <div className="mb-[--half] text-[13px] uppercase tracking-[0.08em] text-ink2">
                What you are signing in for
              </div>
              <div className="flex flex-col gap-[11px] text-[15.5px] leading-snug">
                {UNLOCKS.map(([title, note]) => (
                  <div key={title} className="flex gap-2.5">
                    <span className="mt-[7px] h-[9px] w-[9px] flex-none bg-accent" />
                    <span>
                      <span className="font-semibold">{title}</span>
                      <span className="text-ink2">: {note}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="mb-[--leading] text-[13px] uppercase tracking-[0.08em] text-ink2">
            Sign in
          </div>
          <form onSubmit={onSubmit} className="flex flex-col gap-[--half]">
            {signInError && (
              <p role="alert" className="doubt-block rounded-r text-[14px]">
                {signInError}
              </p>
            )}

            <div>
              <label className="mb-1.5 block text-[13.5px] text-ink2" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                className="input h-[50px] font-mono text-[15px]"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>

            <div>
              <div className="mb-1.5 flex items-baseline justify-between">
                <label className="text-[13.5px] text-ink2" htmlFor="password">
                  Password
                </label>
                <a
                  className="text-[13px]"
                  href={`mailto:${config?.support_email ?? ''}?subject=${encodeURIComponent('Password reset')}`}
                  title="Accounts are issued by your library; ask them to reset it"
                >
                  Forgotten it?
                </a>
              </div>
              <div className="relative">
                <input
                  id="password"
                  className="input h-[50px] pr-16 text-[15px]"
                  type={reveal ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
                <button
                  type="button"
                  className="btn-ghost absolute right-2 top-1/2 -translate-y-1/2 text-[13px]"
                  onClick={() => setReveal((value) => !value)}
                >
                  {reveal ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <label className="flex cursor-pointer items-center gap-2.5 text-[15px]">
              <input
                type="checkbox"
                className="h-4 w-4 accent-[--jl-accent]"
                checked={keep}
                onChange={(event) => setKeep(event.target.checked)}
              />
              Keep me signed in on this workstation
            </label>

            <button
              type="submit"
              className="btn-primary mt-1 h-[52px] text-[16px]"
              disabled={signingIn}
            >
              {signingIn ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <div className="mt-[--leading] flex items-baseline gap-2 border-t border-rule pt-[--half] text-[14.5px]">
            <span className="text-ink2">No account?</span>
            <Link to="/search">Search as a guest</Link>
          </div>
          <p className="ident mt-[--half] uppercase leading-relaxed">
            Institutional sign-in, library cards and proxy access are behind an interface and are
            added without changing this page.
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1200px] flex-none px-[--edge] pb-[calc(2*var(--leading))]">
        <div className="flex flex-wrap justify-between gap-[--leading] border-t border-ink pt-[--leading]">
          <div className="ident leading-loose">
            <div className="text-ink">No account? Searching is open to everyone.</div>
            <div>Guest results carry headline, byline, date, section and page.</div>
          </div>
          <div className="flex flex-wrap gap-[--leading] text-[13px] uppercase tracking-[0.08em]">
            <a href="/docs/handbook/#accessibility">Accessibility · WCAG 2.1 AA</a>
            <a href="/docs/handbook/#privacy">Privacy</a>
            <a href={`mailto:${config?.support_email ?? ''}`}>Ask a librarian</a>
          </div>
        </div>
      </div>
    </div>
  )
}

const UNLOCKS: [string, string][] = [
  ['Page image', 'Full resolution, deep zoom, every region clickable'],
  ['Recognised text', 'What the paper printed, with confidence shown'],
  ['Clippings', 'Crop a passage into a project, citation attached'],
  ['Citation export', 'MLA, Chicago, APA, BibTeX, RIS, frozen on save'],
  ['Browse', 'Any edition by date, page by page'],
]
